import type * as Flows from '../flows/types.no-deps'
import type { FileId } from '../files/types.no-deps'
import type * as Workflows from '../workflows/types.no-deps'
import type { FlowUnitIds } from '../flows/FlowUnitsIds.no-deps'
import type { UnitVersion } from '../units/types.no-deps'
import { map } from '../../lib/functional/array.no-deps'
import { setField } from '../../lib/functional/object.no-deps'
import type { Dictionary } from '../../lib/functional/dictionary.no-deps'
import { assert, type Equals } from 'tsafe'
import type { LoopPlan } from '../flows/LoopPlan.no-deps'
import type { CachedUnitData } from '../units/CachedUnitData.no-deps'
import type { LoopPath } from '../flows/LoopPath.no-deps'

import {
    assumeExhaustion,
    createStringEnum,
    type NominalUUID,
    type Nullable,
    type ReplaceStringLiterals,
    type UUID,
} from '../../lib/typeHelpers.no-deps'
import type { OncePer } from '../schedule/types.no-deps'
import type { Certification, CertificationId } from '../certification/types.no-deps'
import type { EncryptedText } from '../../lib/crypto-node'

export type ReportClientId = NominalUUID<'ReportClientId'>
export const assertReportClientId = (reportId: UUID) => reportId as ReportClientId

export type WithOriginatingReports<T extends {}> = T & {
    // Ancestor reports client ids of a report in order from oldest -> newest
    ancestorReports?: UUID[] | null
}

export type FormResult = number[] | number | string
export interface ReportedForm {
    type: 'location-form' | 'unit-form'
    form: number
    results: Array<{
        question: number
        answer: FormResult
    }>
    note?: string
    unit?: number
}
export interface ReportedLocationForm extends ReportedForm {
    type: 'location-form'
    unit: never
}
export interface ReportedUnitForm extends ReportedForm {
    type: 'unit-form'
    unit: number
}

export interface ReportedIssue {
    type: 'location-issue' | 'unit-issue'
    results: Array<{
        issue: {
            id: number
            value?: {
                type: 'number'
                content: Nullable<number>
            }
            images?: string[]
            note?: string
        }
        fix?: {
            id: number
            images?: string[]
            note?: string
        }
        note?: string
    }>
    unit?: number
}

export interface ReportedLocationIssue extends ReportedIssue {
    type: 'location-issue'
    unit: never
}
export interface ReportedUnitIssue extends ReportedIssue {
    type: 'unit-issue'
    unit: number
}

export function isReportedIssue(item: ReportedIssue | ReportedForm): item is ReportedIssue {
    return item.type === 'location-issue' || item.type === 'unit-issue'
}

export function isReportedForm(item: ReportedIssue | ReportedForm): item is ReportedForm {
    return item.type === 'location-form' || item.type === 'unit-form'
}

export interface ReportedActions {
    action: number
    reportResults: Array<ReportedIssue | ReportedForm>
}

export type ConfirmActionReport = Omit<
    Report,
    | 'id'
    | 'isDeleted'
    | 'data'
    | 'campaign'
    | 'event'
    | 'location'
    | 'status'
    | 'workflow'
    | 'certificationId'
    | 'isConfidential'
>

export type AddableReport = Omit<Report, 'id' | 'isDeleted' | 'isConfidential'>

export const allReportStates = ['open', 'completed', 'cancelled'] as const
export type ReportState = (typeof allReportStates)[number]

/**
 * Intent behind this type is to expose the fields we want to fetch from a report. This exclude all fields that are
 * only used as filters. It is used to avoid omitting the same fields over and over again
 */
export interface Report {
    id: number
    title: Nullable<string>
    clientId: UUID
    author: number
    campaign: Nullable<number>
    coordinates: { lat: number; lon: number }
    endDate: Nullable<Date>
    event: Nullable<number>
    isDeleted: boolean
    location: Nullable<number>
    scheduledInstance: Nullable<UUID>
    startDate: Date
    status: Nullable<number>
    submittedDate: Date
    workflow: Nullable<number>
    data: Nullable<ReportedActions[]>
    answers: Nullable<Answer[]>
    teamDoneFor?: Nullable<number>
    workflowVersion?: Nullable<number>
    subjectUnitId: Nullable<number>
    subjectUnitVersion: Nullable<UUID>
    parentId?: Nullable<number>
    parentClientId?: Nullable<UUID>
    state: ReportState
    progenitor?: Nullable<UUID>
    isValid: boolean
    signature?: Nullable<string>
    signedBy?: Nullable<number>
    signedAt?: Nullable<Date>
    certificationId?: Nullable<CertificationId>
    isConfidential: boolean
}

/**
 *  Contains all the fields that the report table has
 */
export interface DatabaseReport extends Report {
    encryptedTitle: Nullable<EncryptedText>
    encryptedAnswers: Nullable<EncryptedText>
    serverSyncTime: Date
}

export interface DoneReport {
    report: number
    title: Nullable<string>
    encryptedTitle: Nullable<EncryptedText>
    clientId: UUID
    author: number
    workflow: Nullable<number>
    procedure: Nullable<number>
    subjectUnitId: Nullable<number>
    status: Nullable<number>
    commentCount: number
    startDate: Date
    endDate: Date
    // These should be null together or defined together
    scheduledInstance: Nullable<UUID>
    dueDate: Nullable<Date>
    showOverdue: boolean
    description: Nullable<string>
    teamDoneFor: Nullable<number>
    origin?: Nullable<number>
    // Ancestor report client ids of a done report in order from oldest -> newest
    ancestorReports?: UUID[]
    isAction?: Nullable<boolean>
    actionsCreated: number
    actionsCompleted: number
    signature?: Nullable<string>
    signedBy?: Nullable<number>
    signedAt?: Nullable<Date>
    isDeleted?: boolean
    isConfidential: boolean
}

export interface DatabaseFetchReportDetails extends ReportDetails {
    reportClientId: UUID
    answers: Nullable<Answer[]>
}

export interface Answer extends AppAnswer {
    // Restricting value to exclude 'pendingPhotos'
    value: AnswerValue
}

export function isServerAnswer(answer: AppAnswer): answer is Answer {
    return answer.value.type !== 'pendingPhotos'
}

export interface AppAnswer {
    label: string
    value: AppAnswerValue
    timestamp: string | Date
    loopPath?: LoopPath
    tags?: string[]

    activityIds?: {
        activity: number
        unit?: {
            id: number
            instance: UUID
        }
        node?: {
            id: number
            cycle: number
        }
        form?: {
            id: number
            question: number
        }
    }

    unitTypeIds?: number[]
    dynamicElement?: boolean
}

/* If you're updating this set of types. The answer decoders in the Portal's Data.Report module also need to be updated
   to reflect the changes here. */
export type AnswerValue =
    | PhotoValue
    | SelectValue
    | NumberValue
    | StringValue
    | BooleanValue
    | DateValue
    | FilesValue
    | ExtensibleFilesValue
    | UnitsValue
    | ReportsValue
    | SectionValue
    | { type: 'calculation'; value: number }
    | GuideValue

export interface SelectValue {
    type: 'select'
    value: string[]
    rawAnswer?: Flows.CheckboxAnswer | Flows.ImageSelectAnswer | Flows.DropdownAnswer
}
export interface PhotoValue {
    type: 'photos'
    value: string[]
    rawAnswer?: Flows.PhotoAnswer | Flows.SignatureAnswer
}
export interface NumberValue {
    type: 'number'
    value: Nullable<number>
    rawAnswer?: Flows.NumberAnswer
}
export type StringValue = {
    type: 'string'
    value: Nullable<string>
    rawAnswer?: Flows.TextCaptureAnswer | Flows.RadioAnswer | Flows.DateTimeAnswer
}
export interface BooleanValue {
    type: 'boolean'
    value: Nullable<boolean>
    rawAnswer?: Flows.YesNoAnswer | Flows.ConfirmationAnswer
}
export interface DateValue {
    type: 'date'
    value: string
    rawAnswer?: Flows.DateTimeAnswer
}

export interface FilesValue {
    type: 'files'
    value: FileId[]
    rawAnswer?: Flows.FilesSelectAnswer
}

export interface ExtensibleFilesValue {
    type: 'extensible-files'
    value: Flows.ExtensibleFile[]
    rawAnswer?: Flows.ExtensibleFilesAnswer
}

export interface UnitsValue {
    type: 'units'
    value: Flows.SelectedUnits
    rawAnswer?: Flows.UnitSelectAnswer
}

export interface ReportsValue {
    type: 'reports'
    value: Flows.ReportIds
    rawAnswer?: Flows.ReportsAnswer
}
export interface SectionValue {
    type: 'section'
    value: 'completed'
    rawAnswer?: Flows.SectionAnswer
}
export interface GuideValue {
    type: 'guide'
    value: Flows.GuideAnswerValue | Flows.GuideAnswerValue[]
    rawAnswer?: Flows.GuideAnswer
}

/**
 * @deprecated Please use isAnswerOfType instead
 */
export function isUnitsAnswerValue(value: AnswerValue): value is UnitsValue {
    return isAnswerValueOfType('units', value)
}

/**
 * @deprecated Please use isAnswerOfType instead
 */
export function isReportsAnswerValue(value: AnswerValue): value is ReportsValue {
    return isAnswerValueOfType('reports', value)
}

/**
 * @deprecated Please use isAnswerOfType instead
 */
export function isSectionAnswerValue(value: AnswerValue): value is SectionValue {
    return isAnswerValueOfType('section', value)
}

export function isAvailableUnits(selectedUnits: Flows.SelectedUnits): selectedUnits is number[] {
    return selectedUnits !== 'no-units-available'
}

/**
 * @deprecated Please use isAnswerOfType instead
 */
export function isExtensibleFilesValue(value: AnswerValue): value is ExtensibleFilesValue {
    return isAnswerValueOfType('extensible-files', value)
}

export function isAnswerValueOfType<T extends AnswerType>(
    type: T,
    answerValue?: AnswerValue | null
): answerValue is Extract<AnswerValue, { type: T }> {
    return answerValue?.type === type
}

export type AppAnswerValue = AnswerValue | { type: 'pendingPhotos'; value: number[] }
export type AppAnswers = { [key: string]: AppAnswer }

export type AnswerType = (typeof answerTypes)[number]

export const answerTypes = createStringEnum(
    'photos',
    'select',
    'number',
    'string',
    'boolean',
    'date',
    'extensible-files',
    'files',
    'units',
    'section',
    'reports',
    'calculation',
    'guide'
)

// Assert that the types match
assert<Equals<AnswerType, AnswerValue['type']>>()

export function hasRawAnswer(
    answer: AppAnswerValue
): answer is
    | PhotoValue
    | SelectValue
    | NumberValue
    | StringValue
    | BooleanValue
    | DateValue
    | FilesValue
    | UnitsValue
    | SectionValue
    | ReportsValue {
    return answer.type !== 'calculation'
}

export function answerHasMultipleValues(
    value: AnswerValue
): value is SelectValue | PhotoValue | FilesValue | ExtensibleFilesValue | UnitsValue {
    switch (value.type) {
        case 'select':
        case 'photos':
        case 'files':
        case 'extensible-files':
        case 'units':
        case 'reports':
            return true
        case 'number':
        case 'string':
        case 'boolean':
        case 'date':
        case 'section':
        case 'calculation':
        case 'guide':
            return false
        default:
            assumeExhaustion(value)
            return false
    }
}

export type CustomerFacingAnswerType = ReplaceStringLiterals<
    AnswerValue['type'],
    {
        photos: 'photo'
        boolean: 'yes-no'
        string: 'text'
    }
>

// locationInfo available in addition to the `location` field in DatabaseReport as the App is relying on the
export interface ReportWithActions extends DatabaseReport {
    // `location` field so we can't change it
    locationInfo: {
        id: Nullable<number>
        name: Nullable<string>
        latitude: Nullable<number>
        longitude: Nullable<number>
    }
    actions: ReportAction[]
}

export interface ReportAction {
    schedule: UUID
    actionId: UUID
    createdBy: number
    createdAt: Date
    description: string
    dueDate: Date
    done: boolean
    workflow: Nullable<number>
    procedure: Nullable<number>
    users: number[]
    teams: number[]
    reports: ReportActionReport[]
    oncePer: OncePer
    subjectUnitId: Nullable<number>
}

export interface ReportActionReport {
    author: number
    doneAt: Date
    answers: Nullable<Answer[]>
    workflow: Nullable<number>
    id: Nullable<number>
    teamDoneFor: Nullable<number>
}

export interface ReportDetailMap {
    [key: string]: ReportDetails
}

export interface ReportDetails {
    postId: Nullable<UUID>
    reportId: Nullable<number>
    title: Nullable<string>
    unitSubjectId: Nullable<number>
    authorId: Nullable<number>
    procedureId: Nullable<number>
    workflowType: Nullable<Workflows.WorkflowType>
    reportStatusId: Nullable<number>
    isConfidential: Nullable<boolean>
}

export type PhotoUrls = Dictionary<string>

export interface ReportExtraData {
    guideNames: Dictionary<string>
    photoUrls: PhotoUrls
    flowAnswers: Flows.DatabaseAnswer[]
    reportDetails: ReportDetailMap
    subjectUnit: Nullable<UnitVersion>
    flowUnitIds: Nullable<FlowUnitIds>
    loopPlan: LoopPlan
    fromSchedule: Nullable<string>
    cachedUnitData: CachedUnitData
    certification: Nullable<Certification>
    originId?: Nullable<number>
    language?: string
    flowSummary?: Flows.FlowSummary
}

export type ReportGetOutput = WithOriginatingReports<ReportWithActions> & ReportExtraData

export type ReportGetIdInput = { clientId: UUID }
export type ReportGetIdOutput = { id: number }

export type CompactReportGetOutput = Pick<
    ReportGetOutput,
    | 'id'
    | 'title'
    | 'clientId'
    | 'answers'
    | 'workflow'
    | 'workflowVersion'
    | 'subjectUnitId'
    | 'reportDetails'
    | 'photoUrls'
    | 'originId'
    | 'certificationId'
    | 'ancestorReports'
    | 'language'
    | 'flowSummary'
    | 'cachedUnitData'
>

export interface DataCache {
    report: UUID
    flowUnitIds: FlowUnitIds
    loopPlan: LoopPlan
    cachedUnitData: CachedUnitData
}

export function updateReportResults(
    actions: ReportedActions[],
    fn: (reportResult: ReportedIssue | ReportedForm) => ReportedIssue | ReportedForm
): ReportedActions[] {
    return actions.map((action) => setField(action, 'reportResults', map(fn)))
}

type ReportedIssueResult = ReportedIssue['results'][number]

export function updateReportedIssueResults(
    reportedIssue: ReportedIssue,
    fn: (result: ReportedIssueResult) => ReportedIssueResult
): ReportedIssue {
    return setField(reportedIssue, 'results', map(fn))
}

export interface ReportUserCollaborator {
    userId: number
    reportClientId: ReportClientId
}

export type ReportStats = {
    id: number
    location: {
        id: number
        name: string
    }
    activityTemplate: number
    status: number
    startDate: Date
    endDate: Date
    submittedDate: Date
    duration: number
    coordinates?: Nullable<{ lat: number; lon: number }>
    distance?: Nullable<number>
}
type Filter = { procedure?: number; status?: number; author?: number }
type Pagination = { limit: number; offset: number }
export type ReportStatsOptions = Filter & Pagination
export type ReportStatsResult = { reports: ReportStats[]; totalCount: number }
export namespace Types {
    export type date = Date
    export type xCoord = number
    export type yCoord = number
    export type location = number
    export type author = number
    export type ReportItem = {
        product: number
        issues?: number[]
        fixes?: number[]
        images?: string[]
        notes?: string
        pinned?: boolean
    }
    export type items = ReportItem[]
    export type processFlow = number
    export type processFlowStatus = any[]
    export type coordinates = {
        x: number
        y: number
    }
    export type lastMod = Date
}
export type TOldSubmittedReport = {
    coordinates: Types.coordinates
    location: Types.location
    type: 'report' | 'smartdata' | 'text'
    items: Types.items
}

export interface IOldReport {
    id?: number
    event: number
    date: Types.date
    xCoord: Types.xCoord
    yCoord: Types.yCoord
    location: Types.location
    type: string
    author: Types.author
    items: Types.items
    lastMod?: Types.lastMod
    status: number
}

export interface UnitReport {
    id: number
    title: Nullable<string>
    encryptedTitle: Nullable<EncryptedText>
    workflow: number
    status: number
    statusName: string
    postId: string
    submittedDate: Date
    commentCount: number
    actionsCreated: number
    actionsCompleted: number
    subjectUnitId: number
    isDeleted?: boolean
    isConfidential: boolean

    // TODO: Unit Reports V2
    // contributors: number[]

    // TODO: Is this needed?
    // lastModified: Date

    // TODO: What does this mean?
    // createdAt: Date
}

export interface UnitReportFilters {
    statusMap: Dictionary<WorkflowStatusData[]> // Map of status name to WorkflowStatusData
    workflowMap: Dictionary<WorkflowStatusData[]> // Map of workflow Id to WorkflowStatusData
    workflows: Workflow[] // Workflows that have a flow with unit subject type of passed unit
    statuses: Status[] // Status data which belong to workflows above
}

export interface WorkflowStatusData {
    workflowId: number
    workflowName: string
    statusId: number
    statusName: string
}

export type Workflow = {
    workflowId: number
    workflowName: string
}

export type Status = {
    statusId: number
    statusName: string
}

export function isLegacyReport(report: Pick<ReportGetOutput, 'answers' | 'certificationId'>) {
    return report.answers === null && report.certificationId === null
}
